import NiceModal from '@ebay/nice-modal-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppDispatch, useAppSelector } from '@/app/store/hooks';
import {
  rollsActions,
  RollsStatus,
  selectCanBet,
  selectIsPlayerInRound,
  selectIsRollsAutoplayActive,
  selectRollsSelectedBalance,
  selectRollsSelectedCurrency,
  selectRollsStatus,
} from '@/entities/rolls';
import { selectUser } from '@/entities/user';
import { MODALS } from '@/shared/config/modals';
import { floorAmountForCurrency, getGameCurrencyLimits } from '@/shared/lib/currency';
import { BetPanel } from '@/shared/ui/BetPanel';
import { GameActionButton } from '@/shared/ui/GameActionButton';

export function BetControls() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const balance = useAppSelector(selectRollsSelectedBalance);
  const selectedCurrency = useAppSelector(selectRollsSelectedCurrency);
  const user = useAppSelector(selectUser);
  const canBet = useAppSelector(selectCanBet);
  const gameStatus = useAppSelector(selectRollsStatus);
  const isInRound = useAppSelector((state) => selectIsPlayerInRound(state, user?.id ?? ''));
  const autoplayActive = useAppSelector(selectIsRollsAutoplayActive);
  const { minBet, maxBet } = getGameCurrencyLimits('rolls', selectedCurrency);

  const [betAmount, setBetAmount] = useState(minBet);

  // Reset the bet to the currency's minimum whenever the currency (and thus limits) changes.
  useEffect(() => {
    setBetAmount(minBet);
  }, [minBet, selectedCurrency]);

  const isGift = selectedCurrency === 'GIFT';
  const isBetValid = betAmount >= minBet && betAmount <= maxBet && betAmount <= balance;
  // A gift is chosen in the picker (it can even be the first stake), so only
  // `canBet` gates it; cash bets still need a valid numeric amount.
  const isDisabled = !canBet || (!isGift && !isBetValid);

  const setAmount = (value: number) => {
    const clamped = Math.max(0, Math.min(value, Math.min(maxBet, balance)));
    setBetAmount(floorAmountForCurrency(clamped, selectedCurrency));
  };

  const openAutoplay = () => {
    if (autoplayActive) {
      dispatch(rollsActions.stopAutoplay());
      return;
    }
    NiceModal.show(MODALS.AUTOPLAY, {
      currency: selectedCurrency,
      balance,
      minBet,
      maxBet,
      onStart: (config: { betAmount: number; numberOfGames: number }) =>
        dispatch(rollsActions.startAutoplay(config)),
    });
  };

  const placeBet = (amount: number, giftId?: string) => {
    const action = isInRound ? rollsActions.requestAddBet : rollsActions.placeBet;
    dispatch(action({ amount, currency: selectedCurrency, giftId }));
  };

  const handlePlaceBet = () => {
    if (isDisabled) return;
    // A gift is added by picking an item; the backend values it from the gift,
    // so only its id is sent (amount is ignored for gift stakes).
    if (isGift) {
      NiceModal.show(MODALS.GIFT_SELECT, {
        onSelect: (gift: { id: string }) => placeBet(0, gift.id),
      });
      return;
    }
    placeBet(betAmount);
  };

  const buttonLabel =
    gameStatus === RollsStatus.SPINNING
      ? t('rolls.btnSpinning')
      : gameStatus === RollsStatus.FINISHED
        ? t('rolls.btnNextRound')
        : isGift
          ? t('rolls.btnChooseGift')
          : isInRound
            ? t('rolls.btnAddBet', { amount: betAmount, currency: selectedCurrency })
            : t('rolls.btnPlaceBet', { amount: betAmount, currency: selectedCurrency });

  return (
    <div className="bet-controls">
      <BetPanel
        currency={selectedCurrency}
        onCurrencyChange={(currency) => dispatch(rollsActions.setSelectedCurrency(currency))}
        onSelectAmount={setAmount}
        maxAmount={Math.min(maxBet, balance)}
        disabled={!canBet}
        onAutoplay={openAutoplay}
        autoplayActive={autoplayActive}
      />

      <GameActionButton
        label={buttonLabel}
        disabled={isDisabled}
        onClick={handlePlaceBet}
      />
    </div>
  );
}
